import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, RotateCcw } from "lucide-react";
import SmartDropZone from "../components/SmartDropZone";
import AnalysisResultCard from "../components/AnalysisResultCard";
import { AIProcessingOverlay } from "../components/AIProcessingOverlay";
import { useDocumentContext } from "../context/DocumentContext";
import { getApiBase } from "../config";

// --- CONFIGURATION ---
const API_BASE_URL = getApiBase();

// --- TYPES ---
interface AnalysisData {
  document_analysis: Array<{
    document_type: string;
    primary_purpose: string;
    start_page: number | string;
    end_page: number | string;
  }>;
  jurisdiction_and_context: {
    jurisdiction_state: string;
    jurisdiction_court: string;
    case_type: string;
    defendant_residence_state: string;
  };
  redaction_requirements: {
    governing_law: string;
    mandatory_redactions: Array<{ field: string; format: string }>;
  };
}

type AIState = "idle" | "starting" | "working" | "done";

const STEPS = [
  "Uploading PDF…",
  "Reading document…",
  "Detecting jurisdiction…",
  "Classifying filing type…",
  "Extracting redaction rules…",
  "Finalizing results…",
];

async function analyzeViaApi(file: File, forceReanalyze: boolean) {
  const fd = new FormData();
  fd.append("file", file);

  const res = await fetch(
    `${API_BASE_URL}/analyze?force_reanalyze=${forceReanalyze ? "true" : "false"}`,
    { method: "POST", body: fd },
  );

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const msg =
      data?.detail || data?.message || `Analyze failed (${res.status})`;
    throw new Error(msg);
  }

  return data;
}

async function fetchPolicyRules(stateCode: string) {
  const res = await fetch(`${API_BASE_URL}/policy/${stateCode}`);
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const msg =
      data?.detail || data?.message || `Policy fetch failed (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

function formatPolicyRules(rules: Record<string, any>) {
  const items: Array<{ field: string; format: string }> = [];

  for (const [key, cfg] of Object.entries(rules || {})) {
    const action = String(cfg?.action || "REDACT_FULL").toUpperCase();
    const keep = cfg?.keep_visible;
    const desc = cfg?.description ? String(cfg.description) : "";

    let format = action;
    if (action === "REDACT_FULL") format = "Full redaction";
    if (action === "TRUNCATE") format = `Keep last ${keep ?? 4}`;
    if (action === "TRUNCATE_YEAR") format = "Keep year only";
    if (action === "IGNORE_TOLL_FREE") format = "Ignore toll-free numbers";

    const label = desc ? `${desc} · ${format}` : format;

    items.push({
      field: key,
      format: label,
    });
  }

  return items;
}

export default function SmartImportPage() {
  const navigate = useNavigate();
  const { setDocumentContext } = useDocumentContext();

  // --- UI DATA STATE ---
  const [result, setResult] = useState<AnalysisData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // --- ANALYSIS OVERLAY STATE (Premium Siri) ---
  const [aiState, setAiState] = useState<AIState>("idle");
  const [subtitle, setSubtitle] = useState(STEPS[0]);
  const [progress, setProgress] = useState(0);

  const isAnalyzing = aiState !== "idle";

  // Ignore stale responses if user retries quickly
  const runSeq = useRef(0);

  // Rotate subtitle while analyzing (unless we explicitly set it)
  useEffect(() => {
    if (!isAnalyzing) return;

    let i = 0;
    const t = window.setInterval(() => {
      i++;
      setSubtitle((current) => current || STEPS[i % STEPS.length]);
    }, 1400);

    return () => window.clearInterval(t);
  }, [isAnalyzing]);

  const runAnalysis = async (file: File, forceReanalyze: boolean) => {
    const mySeq = ++runSeq.current;

    setError(null);
    setResult(null);

    // Start overlay lifecycle
    setProgress(0.06);
    setSubtitle(forceReanalyze ? "Re-analyzing document…" : "Uploading PDF…");
    setAiState("starting");

    // Avoid flash for fast responses
    const showTimer = window.setTimeout(() => {
      if (runSeq.current === mySeq) setAiState("working");
    }, 250);

    try {
      setProgress(0.18);
      setSubtitle("Warming up the brain…");

      // (Optional) Keep a tiny delay for perceived responsiveness without feeling fake
      await new Promise((resolve) => setTimeout(resolve, 300));

      setProgress(0.35);
      setSubtitle(
        forceReanalyze ? "Re-analyzing document…" : "Analyzing document…",
      );

      const data = (await analyzeViaApi(file, forceReanalyze)) as AnalysisData;

      const mandatory = data?.redaction_requirements?.mandatory_redactions || [];
      const state = data?.jurisdiction_and_context?.jurisdiction_state;

      if (mandatory.length === 0 && state) {
        try {
          const policy = await fetchPolicyRules(state);
          const rules = policy?.rules || {};

          data.redaction_requirements = {
            ...(data.redaction_requirements || { governing_law: "Unknown", mandatory_redactions: [] }),
            mandatory_redactions: formatPolicyRules(rules),
          };
        } catch (e) {
          console.warn("Policy fetch failed:", e);
        }
      }

      // Newer run started; ignore this result
      if (runSeq.current !== mySeq) return;

      setProgress(0.7);
      setSubtitle("Extracting jurisdiction & rules…");

      setResult(data);
      setProgress(1);
      setSubtitle("Done");
      setAiState("done");
    } catch (err: any) {
      if (runSeq.current !== mySeq) return;
      console.error("Upload Error:", err);
      setError(err?.message || "An unexpected network error occurred.");
      setAiState("idle");
    } finally {
      window.clearTimeout(showTimer);

      // Smooth exit
      if (runSeq.current === mySeq) {
        window.setTimeout(() => setAiState("idle"), 220);
        window.setTimeout(() => setProgress(0), 450);
      }
    }
  };

  // --- HANDLERS ---
  const handleFileUpload = async (file: File) => {
    // Reset UI state
    setUploadedFile(file);
    await runAnalysis(file, false);
  };

  const handleRetry = () => {
    if (!uploadedFile) return;

    runSeq.current += 1;
    setError(null);
    setResult(null);

    // Retry should NOT clear the file
    runAnalysis(uploadedFile, false);
  };

  const handleRescan = () => {
    if (!uploadedFile) return;

    runSeq.current += 1;
    setError(null);
    setResult(null);

    runAnalysis(uploadedFile, true);
  };

  const handleProceedToWizard = () => {
    if (result && uploadedFile) {
      setDocumentContext(uploadedFile, result);
      navigate("/wizard");
    }
  };

  return (
    <div className="relative min-h-[600px] max-w-5xl mx-auto space-y-8 animate-in fade-in duration-500 p-6">
      {/* --- AI PROCESSING OVERLAY (Premium Siri) --- */}
      <AIProcessingOverlay
        isVisible={isAnalyzing}
        title={aiState === "done" ? "Done" : "Working on Document"}
        subtitle={subtitle || "Mike is analyzing your document..."}
        progress={progress}
      />

      {/* --- HEADER --- */}
      {!result && (
        <div className="text-center mb-10 transition-opacity duration-300">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
            Smart Import
          </h1>
          <p className="text-slate-400 text-lg">
            Upload a filing to automatically detect jurisdiction and redaction
            rules.
          </p>
        </div>
      )}

      {/* --- ERROR BANNER --- */}
      {error && (
        <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-6 flex items-start gap-4 animate-in slide-in-from-top-2">
          <div className="p-2 bg-red-800 rounded-full text-red-100 shadow-lg shadow-red-900/20">
            <AlertCircle className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-red-200 mb-1">
              Analysis Failed
            </h3>
            <p className="text-red-300 text-sm mb-4 leading-relaxed">{error}</p>
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-red-700 rounded-lg text-sm font-medium text-red-300 hover:bg-slate-700 hover:text-white transition-all shadow-sm"
            >
              <RotateCcw className="w-4 h-4" /> Try Again
            </button>
          </div>
        </div>
      )}

      {/* --- STATE 1: DROP ZONE --- */}
      {!result && !error && (
        <div
          className={`py-8 transition-all duration-700 ease-in-out ${
            isAnalyzing
              ? "opacity-0 blur-md scale-95 pointer-events-none"
              : "opacity-100 blur-0 scale-100"
          }`}
        >
          <SmartDropZone
            onFileSelect={handleFileUpload}
            disabled={isAnalyzing}
          />
        </div>
      )}

      {/* --- STATE 2: RESULT CARD --- */}
      {result && (
        <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={handleRetry}
              className="text-slate-400 hover:text-white flex items-center gap-2 text-sm font-medium transition-colors group"
            >
              <div className="p-1.5 rounded-full bg-slate-800 group-hover:bg-slate-700 transition-colors">
                <RotateCcw className="w-3.5 h-3.5" />
              </div>
              Upload Different File
            </button>
          </div>

          <AnalysisResultCard
            result={result}
            onConfirm={handleProceedToWizard}
            onRescan={handleRescan}
          />
        </div>
      )}
    </div>
  );
}
